import React, { useRef } from 'react';
import { Invoice } from '../types/invoice.ts';
import { X, Printer } from 'lucide-react';

interface InvoiceReportProps {
  invoices: Invoice[];
  onClose: () => void;
}

const InvoiceReport: React.FC<InvoiceReportProps> = ({ invoices, onClose }) => {
  const reportRef = useRef<HTMLDivElement>(null);

  // Format date in American format with proper timezone handling
  const formatDateForReport = (date: Date | string) => {
    if (typeof date === 'string') {
      // Check if it's a BTC date range format (YYYY-MM-DD to YYYY-MM-DD)
      const dateRangeMatch = date.match(/^(\d{4}-\d{2}-\d{2})\s+to\s+(\d{4}-\d{2}-\d{2})$/);
      if (dateRangeMatch) {
        // Use the first date from the range
        const [year, month, day] = dateRangeMatch[1].split('-').map(Number);
        const localDate = new Date(year, month - 1, day);
        return localDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      
      // If it's in standard YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        const [year, month, day] = date.split('-').map(Number);
        const localDate = new Date(year, month - 1, day);
        return localDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      
      // Try parsing other string formats
      const parsedDate = new Date(date);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      
      return date;
    }
    
    // If it's already a Date object
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handlePrint = () => {
    // Create a new window with just the report content
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      alert('Please allow popups to print the report');
      return;
    }

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice Report - 7Twenty4 Services LLC</title>
          <style>
            @page {
              margin: 0.75in;
              size: letter;
            }
            
            * {
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              margin: 0;
              padding: 20px;
              color: #2d3748;
              line-height: 1.5;
              background: white;
            }
            
            .header {
              text-align: center;
              margin-bottom: 40px;
              border-bottom: 3px solid #2b6cb0;
              padding-bottom: 20px;
            }
            
            .header h1 {
              font-size: 32px;
              font-weight: 700;
              margin: 0 0 8px 0;
              color: #1a365d;
              text-transform: uppercase;
              letter-spacing: 1px;
            }
            
            .header .subtitle {
              font-size: 18px;
              color: #2b6cb0;
              font-weight: 600;
              margin: 8px 0;
            }
            
            .header .date {
              font-size: 14px;
              color: #718096;
              margin: 5px 0;
              font-style: italic;
            }
            
            .summary {
              background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
              border: 2px solid #e2e8f0;
              border-radius: 8px;
              padding: 25px;
              margin: 30px 0;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            
            .summary-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 30px;
              max-width: 400px;
              margin: 0 auto;
            }
            
            .summary-item {
              text-align: center;
              padding: 15px;
              background: white;
              border-radius: 6px;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            }
            
            .summary-label {
              font-size: 14px;
              color: #4a5568;
              font-weight: 500;
              margin-bottom: 5px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            
            .summary-value {
              font-size: 24px;
              font-weight: 700;
              color: #1a365d;
              margin: 0;
            }
            
            .table-container {
              margin: 30px 0;
              border-radius: 8px;
              overflow: hidden;
              box-shadow: 0 4px 6px rgba(0,0,0,0.1);
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 13px;
              background: white;
            }
            
            thead {
              background: linear-gradient(135deg, #2b6cb0 0%, #2c5282 100%);
            }
            
            th {
              padding: 15px 12px;
              text-align: left;
              font-weight: 600;
              color: white;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              font-size: 12px;
              border: none;
            }
            
            td {
              padding: 12px;
              border-bottom: 1px solid #e2e8f0;
              vertical-align: top;
            }
            
            tbody tr {
              transition: background-color 0.2s;
            }
            
            tbody tr:nth-child(even) {
              background-color: #f8fafc;
            }
            
            tbody tr:hover {
              background-color: #edf2f7;
            }
            
            .amount {
              text-align: right;
              font-weight: 600;
              color: #1a365d;
            }
            
            .total-row {
              background: linear-gradient(135deg, #edf2f7 0%, #e2e8f0 100%) !important;
              font-weight: 700;
              border-top: 2px solid #2b6cb0;
            }
            
            .total-row td {
              padding: 15px 12px;
              font-size: 14px;
              color: #1a365d;
            }
            
            .invoice-number {
              font-weight: 600;
              color: #2b6cb0;
            }
            
            .job-name {
              color: #2d3748;
              font-weight: 500;
            }
            
            .client-name {
              color: #4a5568;
              font-weight: 500;
            }
            
            .footer {
              text-align: center;
              margin-top: 50px;
              padding-top: 20px;
              border-top: 2px solid #e2e8f0;
              color: #718096;
            }
            
            .footer p {
              margin: 5px 0;
              font-size: 13px;
            }
            
            .footer .company-info {
              font-weight: 600;
              color: #4a5568;
              margin-bottom: 8px;
            }
            
            /* Print-specific styles */
            @media print {
              body {
                padding: 0;
              }
              
              .summary {
                break-inside: avoid;
              }
              
              table {
                break-inside: auto;
              }
              
              tr {
                break-inside: avoid;
                break-after: auto;
              }
              
              thead {
                display: table-header-group;
              }
              
              tfoot {
                display: table-footer-group;
              }
              
              .header {
                break-after: avoid;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>7Twenty4 Services LLC</h1>
            <p class="subtitle">Invoice Report</p>
            <p class="date">Generated on ${currentDate}</p>
          </div>

          <div class="summary">
            <div class="summary-grid">
              <div class="summary-item">
                <p class="summary-label">Total Invoices</p>
                <p class="summary-value">${invoices.length}</p>
              </div>
              <div class="summary-item">
                <p class="summary-label">Total Amount</p>
                <p class="summary-value">${formatCurrency(totalAmount)}</p>
              </div>
            </div>
          </div>

          <div class="table-container">
            <table>
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Job Name</th>
                  <th>Client</th>
                  <th>Week Ending</th>
                  <th>Due Date</th>
                  <th class="amount">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${invoices.map(invoice => `
                  <tr>
                    <td class="invoice-number">${invoice.invoiceNumber}</td>
                    <td class="job-name">${invoice.jobName}</td>
                    <td class="client-name">${invoice.clientName}</td>
                    <td>${invoice.weekEnding ? formatDateForReport(invoice.weekEnding) : formatDateForReport(invoice.invoiceDate)}</td>
                    <td>${formatDateForReport(invoice.dueDate)}</td>
                    <td class="amount">${formatCurrency(invoice.totalAmount)}</td>
                  </tr>
                `).join('')}
              </tbody>
              <tfoot>
                <tr class="total-row">
                  <td colspan="5" style="text-align: right;">TOTAL:</td>
                  <td class="amount">${formatCurrency(totalAmount)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div class="footer">
            <p class="company-info">7Twenty4 Services LLC</p>
            <p>200 Greenlea St, Pulaski, VA 24301</p>
            <p>Phone: (972) 333-0913 | Email: kim@7twenty4services.com</p>
          </div>

          <script>
            // Auto-print when page loads
            window.onload = function() {
              window.print();
              // Close the window after printing (user can cancel this)
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const totalAmount = invoices.reduce((sum, invoice) => sum + invoice.totalAmount, 0);
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden print-modal">
        {/* Header with controls */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200 no-print">
          <h2 className="text-xl font-semibold text-gray-900">Invoice Report</h2>
          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </button>
            <button
              onClick={onClose}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <X className="h-4 w-4 mr-2" />
              Close
            </button>
          </div>
        </div>

        {/* Report content */}
        <div className="overflow-auto max-h-[calc(90vh-80px)]">
          <div ref={reportRef} className="p-8 print:p-6 print-report">
            {/* Report header */}
            <div className="text-center mb-8 print:mb-6">
              <h1 className="text-2xl font-bold text-gray-900 print:text-xl">7Twenty4 Services LLC</h1>
              <p className="text-gray-600">Invoice Report</p>
              <p className="text-sm text-gray-500">Generated on {currentDate}</p>
            </div>

            {/* Summary */}
            <div className="mb-6 print:mb-4">
              <div className="bg-gray-50 print:bg-white print:border print:border-gray-300 rounded-lg p-4 print:p-3 summary-box">
                <div className="grid grid-cols-2 gap-4 print:gap-2">
                  <div>
                    <p className="text-sm text-gray-600">Total Invoices:</p>
                    <p className="text-lg font-semibold text-gray-900 print:text-base">{invoices.length}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Amount:</p>
                    <p className="text-lg font-semibold text-gray-900 print:text-base">{formatCurrency(totalAmount)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300 print:text-sm">
                <thead>
                  <tr className="bg-gray-50 print:bg-gray-100">
                    <th className="border border-gray-300 px-4 py-3 print:px-2 print:py-2 text-left text-sm font-medium text-gray-700">
                      Invoice #
                    </th>
                    <th className="border border-gray-300 px-4 py-3 print:px-2 print:py-2 text-left text-sm font-medium text-gray-700">
                      Job Name
                    </th>
                    <th className="border border-gray-300 px-4 py-3 print:px-2 print:py-2 text-left text-sm font-medium text-gray-700">
                      Client
                    </th>
                    <th className="border border-gray-300 px-4 py-3 print:px-2 print:py-2 text-left text-sm font-medium text-gray-700">
                      Week Ending
                    </th>
                    <th className="border border-gray-300 px-4 py-3 print:px-2 print:py-2 text-left text-sm font-medium text-gray-700">
                      Due Date
                    </th>
                    <th className="border border-gray-300 px-4 py-3 print:px-2 print:py-2 text-right text-sm font-medium text-gray-700">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice, index) => (
                    <tr key={invoice.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50 print:bg-white'}>
                      <td className="border border-gray-300 px-4 py-3 print:px-2 print:py-2 text-sm font-medium text-gray-900">
                        {invoice.invoiceNumber}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 print:px-2 print:py-2 text-sm text-gray-900">
                        {invoice.jobName}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 print:px-2 print:py-2 text-sm text-gray-900">
                        {invoice.clientName}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 print:px-2 print:py-2 text-sm text-gray-900">
                        {invoice.weekEnding ? formatDateForReport(invoice.weekEnding) : formatDateForReport(invoice.invoiceDate)}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 print:px-2 print:py-2 text-sm text-gray-900">
                        {formatDateForReport(invoice.dueDate)}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 print:px-2 print:py-2 text-sm text-gray-900 text-right font-medium">
                        {formatCurrency(invoice.totalAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100 print:bg-gray-200">
                    <td colSpan={5} className="border border-gray-300 px-4 py-3 print:px-2 print:py-2 text-sm font-medium text-gray-900 text-right">
                      Total:
                    </td>
                    <td className="border border-gray-300 px-4 py-3 print:px-2 print:py-2 text-sm font-bold text-gray-900 text-right">
                      {formatCurrency(totalAmount)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Footer */}
            <div className="mt-8 print:mt-6 text-center text-sm text-gray-500">
              <p>7Twenty4 Services LLC | 200 Greenlea St, Pulaski, VA 24301</p>
              <p>Phone: (972) 333-0913 | Email: kim@7twenty4services.com</p>
            </div>
          </div>
        </div>
      </div>

      {/* Print styles */}
      <style jsx global>{`
        @media print {
          /* Hide everything except our report */
          body * {
            visibility: hidden;
          }
          .print-report, .print-report * {
            visibility: visible;
          }
          .print-report {
            position: absolute;
            left: 0;
            top: 0;
            width: 100% !important;
            height: auto !important;
            max-width: none !important;
            max-height: none !important;
            margin: 0 !important;
            padding: 20px !important;
            background: white !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            overflow: visible !important;
          }
          
          /* Hide controls */
          .no-print {
            display: none !important;
          }
          
          /* Reset modal styles for print */
          .print-modal {
            position: static !important;
            background: none !important;
            max-width: none !important;
            max-height: none !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            border-radius: 0 !important;
          }
          
          /* Table styles */
          table {
            width: 100% !important;
            border-collapse: collapse !important;
            margin: 20px 0 !important;
            page-break-inside: auto;
          }
          
          th, td {
            border: 1px solid #333 !important;
            padding: 8px !important;
            font-size: 12px !important;
            line-height: 1.4 !important;
          }
          
          th {
            background-color: #f0f0f0 !important;
            font-weight: bold !important;
          }
          
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          
          thead {
            display: table-header-group !important;
          }
          
          tfoot {
            display: table-footer-group !important;
            background-color: #e0e0e0 !important;
          }
          
          /* Typography */
          h1 {
            font-size: 24px !important;
            margin: 0 0 10px 0 !important;
          }
          
          h2 {
            font-size: 18px !important;
            margin: 20px 0 10px 0 !important;
          }
          
          p {
            margin: 5px 0 !important;
            font-size: 12px !important;
          }
          
          /* Summary box */
          .summary-box {
            border: 1px solid #333 !important;
            padding: 15px !important;
            margin: 20px 0 !important;
            background-color: #f8f8f8 !important;
          }
          
          /* Page breaks */
          .page-break {
            page-break-before: always;
          }
          
          /* Ensure colors print */
          body {
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
        }
      `}</style>
    </div>
  );
};

export default InvoiceReport;